import {
  Box,
  Flex,
  Icon,
  Img,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { ImageOff } from "lucide-react";
import { useSession } from "next-auth/react";
import { useTranslation } from "next-i18next";
import getConfig from "next/config";
import { useRouter } from "next/router";
import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, LogIn, Mail, Phone } from "react-feather";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import ReactPlayer from "react-player";

import ButtonAnimated from "@/components/common/ButtonAnimated";
import { ImageState } from "@/components/common/Image";
import Loader from "@/components/common/Loader";
import { Product } from "@/types/product";
import { formatPrice } from "@/utils/helpers";
import useIsMobile from "@/hooks/useIsMobile";

const { publicRuntimeConfig } = getConfig();

type Props = {
  product: Product;
  initialSlide: number;
  isUserProduct: boolean;
  isOpen: boolean;
  onClose: () => void;
};

const FullPhotosModal = ({
  product,
  initialSlide,
  isUserProduct,
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation();
  const { status } = useSession();
  const router = useRouter();
  const [showPhone, setShowPhone] = useState(false);
  const [loginToMessage, setLoginToMessage] = useState(false);
  const isMobile = useIsMobile();

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: false,
      speed: 500,
      initialSlide: initialSlide,
      slidesToShow: 1,
      slidesToScroll: 1,
      prevArrow: (
        <Icon
          as={ChevronLeft}
          w={12}
          h={12}
          color="black"
          opacity={1}
          _hover={{
            opacity: isMobile ? 1 : 0.5,
            color: "black",
          }}
          zIndex={1000}
        />
      ),
      nextArrow: (
        <Icon
          as={ChevronRight}
          w={12}
          h={12}
          color="black"
          opacity={1}
          _hover={{
            opacity: isMobile ? 1 : 0.5,
            color: "black",
          }}
          zIndex={1000}
        />
      ),
    }),
    [initialSlide, isMobile]
  );

  const sortedPhotos = useMemo(() => {
    if (product?.photos?.length > 0)
      return product?.photos?.sort((a, b) => +a?.order - +b?.order);
    return [];
  }, [product?.photos]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"full"}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent borderRadius={0} className="modal-large">
        <ModalHeader borderRadius={0}>
          <Flex
            flexDirection={{ base: "column", md: "row" }}
            justifyContent={{ base: "center", md: "space-between" }}
            alignItems={{ base: "flex-start", md: "center" }}
          >
            <Flex
              fontSize={{ base: "lg", md: "2xl" }}
              fontWeight={"600"}
              flexDirection={{ base: "column", md: "row" }}
            >
              <Text>{product?.name}</Text>
              <Text color={"green.100"} ml={{ base: 0, md: "20px" }}>
                {product?.free
                  ? t("products.free")
                  : formatPrice(product?.price, product.currency)}
              </Text>
            </Flex>
            {!isUserProduct && (
              <Flex
                mr={{ base: 0, md: 8 }}
                flexDirection={{ base: "column", md: "row" }}
                gap={{ base: 1, md: 0 }}
              >
                <ButtonAnimated
                  text={
                    <Flex align={"center"} justifyContent="center">
                      <Icon
                        as={loginToMessage ? LogIn : Mail}
                        w={{ base: "14px", md: "20px" }}
                        h={{ base: "14px", md: "20px" }}
                        mr={2}
                        verticalAlign="-6px"
                      />
                      <Text fontSize={{ base: "sm", md: "md" }}>
                        {!loginToMessage
                          ? t("product.sendMessage")
                          : t("signin.login")}
                      </Text>
                    </Flex>
                  }
                  variant="blue"
                  onClick={() =>
                    status === "authenticated" ? null : router.push("/signin")
                  }
                  onMouseEnter={() => {
                    if (status === "unauthenticated") setLoginToMessage(true);
                  }}
                  onMouseLeave={() => {
                    if (status === "unauthenticated") setLoginToMessage(false);
                  }}
                  mr={{ base: 0, md: 2 }}
                  ml={{ base: -2, md: 0 }}
                  width={{ base: "100%", md: 300 }}
                />
                {product?.garage?.phone && (
                  <ButtonAnimated
                    text={
                      <>
                        <Icon
                          as={Phone}
                          w={{ base: "14px", md: "20px" }}
                          h={{ base: "14px", md: "20px" }}
                          mr={2}
                          verticalAlign="-6px"
                        />
                        {showPhone ? product?.garage?.phone : t("product.call")}
                      </>
                    }
                    onClick={() => setShowPhone(true)}
                    width={{ base: "100%", md: 300 }}
                  />
                )}
              </Flex>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton
          size={"lg"}
          zIndex={10}
          bg={{ base: "rgba(0, 0, 0, 0.5)", md: "transparent" }}
          color={{ base: "white", md: "inherit" }}
          borderRadius={{ base: "md", md: "md" }}
          _hover={{
            bg: { base: "rgba(0, 0, 0, 0.7)", md: "gray.100" },
          }}
        />
        <ModalBody
          alignContent={"center"}
          p={{ base: 0, md: "50px" }}
          height={{ base: "100vh", md: "auto" }}
        >
          <Box className="slider-dark" height={{ base: "auto", md: "auto" }}>
            <Slider {...sliderSettings}>
              {sortedPhotos?.map((photo, index) => (
                <Image fileName={photo?.filename} key={index} alt="" />
              ))}

              {product?.videoUrl && (
                <Flex
                  justifyContent={"center"}
                  alignItems={"center"}
                  height={{ base: "30vh", md: "75vh" }}
                  pl={{ base: 0, md: 5 }}
                  pr={{ base: 0, md: 5 }}
                  mt={{ base: 0, md: 0 }}
                >
                  <ReactPlayer
                    url={product.videoUrl}
                    width={isMobile ? "90%" : "100%"}
                    height={"100%"}
                    controls={true}
                    style={{
                      alignSelf: "center",
                      justifySelf: "center",
                      justifyContent: "center",
                      alignItems: "center",
                      display: "flex",
                      flexDirection: "column",
                      flexWrap: "wrap",
                      flexGrow: 1,
                      flexShrink: 1,
                      flexBasis: "auto",
                    }}
                  />
                </Flex>
              )}
            </Slider>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const Image = ({
  fileName,
  alt,
}: {
  fileName: string;
  alt: string;
}): React.ReactElement => {
  const [imageState, setImageState] = useState(ImageState.LOADING);

  return (
    <Box width={"100%"} h={{ base: "auto", md: "calc(100vh - 200px)" }}>
      {imageState === ImageState.LOADING && <Loader w="100%" h="100%" />}
      {imageState === ImageState.ERROR && (
        <Flex align="center" justifyContent={"center"} w="100%" h="100%">
          <ImageOff size={100} />
        </Flex>
      )}
      <Img
        src={`${publicRuntimeConfig.apiUrl}/uploads/${fileName}`}
        alt={alt}
        visibility={
          imageState === ImageState.LOADING || imageState === ImageState.ERROR
            ? "hidden"
            : "visible"
        }
        objectFit="contain"
        maxW={{ base: "90%", md: "100%" }}
        h="100%"
        mx="auto"
        onLoad={() => setImageState(ImageState.LOADED)}
        onError={() => setImageState(ImageState.ERROR)}
      />
    </Box>
  );
};

export default FullPhotosModal;
